import * as React from 'react';

import { useQuery } from '@apollo/client';
import ReportPQNormsQuery from '@graphql/queries/ReportPQNormsQuery';
import {
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  useTheme,
} from '@mui/material';
import useCustomProductNames from 'hooks/useCustomProductNames';
import usePqTransform from 'hooks/usePqTransform';
import useReportSummary, {
  ReportSummaryDetailNode,
} from 'hooks/useReportSummary';
import { useTranslation } from 'react-i18next';

import { getProductName } from '../../Report/utils';
import useStyles from '../useStyles';

interface Props {
  projectId: number;
  reportId: string;
}

const StackRankPqRange: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { projectId, reportId } = props;

  const productNames = useCustomProductNames({ projectId: Number(projectId) });
  const classes = useStyles(useTheme());
  const pq = usePqTransform();

  const {
    loading: reportSummaryLoading,
    error: reportSummaryError,
    data: reportSummaryData,
    pqRange,
    topProduct,
    bottomProduct,
  } = useReportSummary(reportId);

  const {
    loading: normsLoading,
    error: normsError,
    data: normsData,
  } = useQuery<reports.ReportPQNormsResponse>(ReportPQNormsQuery, {
    variables: {
      reportID: reportId,
    },
  });

  if (reportSummaryLoading || normsLoading) {
    return <Skeleton variant="rectangular" height={500} animation="wave" />;
  }

  if (normsError || reportSummaryError) {
    return <></>;
  }

  const rankHighestText = (
    product: ReportSummaryDetailNode,
    normsData: reports.ReportPQNormsResponse,
  ) => {
    const percentile25 =
      parseFloat(normsData.allRpCategoryNorms.nodes.find(Boolean)?.q1) || 0;
    const percentile75 =
      parseFloat(normsData.allRpCategoryNorms.nodes.find(Boolean)?.q3) || 7;

    if (
      product.node.pq &&
      percentile75 &&
      parseFloat(product.node.pq) > percentile75
    ) {
      return ' ranked highest among all report products and above the 75th percentile of the category.';
    } else if (
      product.node.pq &&
      percentile75 &&
      parseFloat(product.node.pq) < percentile25
    ) {
      return ' ranked highest among all report products, but below the 25th percentile of the category.';
    } else {
      return ' ranked highest among all report products.';
    }
  };

  const rankLowestText = (
    product: ReportSummaryDetailNode,
    normsData: reports.ReportPQNormsResponse,
  ) => {
    const percentile25 =
      parseFloat(normsData.allRpCategoryNorms.nodes.find(Boolean)?.q1) || 0;
    const percentile75 =
      parseFloat(normsData.allRpCategoryNorms.nodes.find(Boolean)?.q3) || 7;

    if (
      product.node.pq &&
      percentile75 &&
      parseFloat(product.node.pq) > percentile75
    ) {
      return ' ranked lowest among all report products, but above the 75th percentile of the category.';
    } else if (
      product.node.pq &&
      percentile75 &&
      parseFloat(product.node.pq) < percentile25
    ) {
      return ' ranked lowest among all report products and below the 25th percentile of the category.';
    } else {
      return ' ranked lowest among all report products.';
    }
  };

  if (!pqRange) {
    return <div></div>;
  }

  const stackRankRange = `${t('reports.summary.stackRankRange')}`;
  const transformedRange = pq.transform(pqRange + 1) - pq.transform(1);

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <CardHeader title={stackRankRange} className={classes.header} />
        <ul>
          {pqRange && (
            <li>{`All products are within ${transformedRange.toFixed(2)} ${pq.label} range`}</li>
          )}
          {!!topProduct && (
            <li>
              <span className={classes.mainText}>
                {getProductName({
                  productNames,
                  productId: topProduct.node.productByProductId.id,
                  version: topProduct.node.version,
                })}
              </span>
              <span>{rankHighestText(topProduct, normsData)}</span>
            </li>
          )}
          {!!bottomProduct && (
            <li>
              <span className={classes.mainText}>
                {getProductName({
                  productNames,
                  productId: bottomProduct.node.productByProductId.id,
                  version: bottomProduct.node.version,
                })}
              </span>
              <span>{rankLowestText(bottomProduct, normsData)}</span>
            </li>
          )}
        </ul>
      </CardContent>
    </Card>
  );
};

export default StackRankPqRange;
